<script setup lang="ts">
/**
 * Shows that an offer has already been acted on, in two shapes: a <peppermill-badge> for the offer cards in the grid,
 * and a full-width banner for the single-offer page. Both draw their copy from src\helpers\getOfferRedemption, so the
 * two pages cannot describe the same offer differently.
 *
 * Renders nothing at all for an offer that has not been redeemed, so callers can drop it in unconditionally.
 *
 * @changelog
 *
 * 2026-09-08 (0.1.0) (jhoward)
 *   - Add App\components\offers\OfferRedeemedStatus.vue, replacing the floating check_circle icon that
 *     src\components\offers\Offer.vue and src\views\Offers\ViewOffer.vue each carried their own copy of.
 */

import PeppermillBadge from '@framework/components/PeppermillBadge.vue';
import type { PropType } from 'vue';
import { computed } from 'vue';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import getOfferRedemption from '@/helpers/getOfferRedemption';

const props = defineProps({
  offer: {
    type: Object as PropType<OpcOffer>,
    required: true,
  },
  //Full-width banner for the detail page. The compact badge is used on cards.
  isBanner: {
    type: Boolean,
    default: false,
  },
});

const redemption = computed(() => getOfferRedemption(props.offer));
</script>

<template>
  <template v-if="redemption.isRedeemed">
    <!--Banner: the badge carries the state word, the prose beside it carries who and what.-->
    <div v-if="isBanner" class="offer_redeemed--banner">
      <peppermill-badge
        :label="redemption.label"
        :icon="redemption.icon"
        variant="success"
        mod="solid"
      ></peppermill-badge>
      <p class="offer_redeemed--detail">
        <template v-if="redemption.redeemedBy">{{ redemption.label }} by {{ redemption.redeemedBy }}</template>
        <template v-else>This offer has already been {{ redemption.label.toLowerCase() }}</template>
        <span v-if="redemption.prize" class="offer_redeemed--prize">{{ redemption.prize }}</span>
      </p>
    </div>

    <!--Card: one badge, wide enough to say who, with the prize underneath when there is one.-->
    <div v-else class="offer_redeemed--badge_row">
      <peppermill-badge :icon="redemption.icon" variant="success" mod="subtle" :tooltip="redemption.summary">
        <template v-if="redemption.redeemedBy">{{ redemption.label }} by {{ redemption.redeemedBy }}</template>
        <template v-else>{{ redemption.label }}</template>
      </peppermill-badge>
      <span v-if="redemption.prize" class="offer_redeemed--prize">{{ redemption.prize }}</span>
    </div>
  </template>
</template>

<style scoped>
.offer_redeemed--badge_row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25em 0.5em;
  padding: 0.5em 0 0.25em;
}

/*Sits above the offer title, so it is the first thing read on a detail page for an offer that is already gone.*/
.offer_redeemed--banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em 0.75em;
  margin-bottom: 1em;
  padding: 0.75em 1em;
  border-left: 4px solid rgb(var(--status-success));
  border-radius: 0.25em;
  background-color: rgba(var(--status-success), 0.12);
}

.offer_redeemed--banner .offer_redeemed--detail {
  margin: 0;
  min-width: 0;
}

/*Full-contrast body text, not the success colour: green on a green tint is the one place this pattern goes
  unreadable, and the badge beside it already carries the status colour.*/
.offer_redeemed--prize {
  display: block;
  font-size: 0.875em;
  opacity: 0.75;
}

.offer_redeemed--badge_row .offer_redeemed--prize {
  display: inline;
}
</style>
